import { plz } from '@/utils'
import { userName } from '@/utils/data/agents'

const UNKNOWN_CUSTOMER = {
  id: null,
  name: 'Client inconnu'
}

const UNKNOWN_SITE = {
  id: null,
  name: 'Site inconnu',
  customer: UNKNOWN_CUSTOMER
}

export default {
  /**
   * Mapping des agents
   * @param state
   * @return {*}
   */
  agents: (state) => state.agents.map((a) => {
    const hoursLimit = _.get(a, ['agentInfos', 'activeContract', 'hoursLimit'])
    return {
      ...a,
      workingLimit: hoursLimit
        ? Math.round(hoursLimit * 52 / 12 * 100) / 100
        : 0
    }
  }),

  /**
   * Trouve un agent depuis son slug
   * @param state
   * @param agents
   * @returns {function(*): *}
   */
  getAgent: (state, { agents }) => (slug) => agents.find((s) => s.slug === slug),

  /**
   * Liste des agents indexés par ID
   * @param state
   * @param agents
   * @returns {*}
   */
  agentById: (state, { agents }) => _.keyBy(agents, 'id'),

  /**
   * Liste de tous les sites triés par ordre alphabétique
   * @param clients
   * @return {*|Array}
   */
  sites: ({ sites = [] }) => _.sortBy(sites, 'name'),

  /**
   * Trouve un site depuis son slug
   * @param state
   * @returns {function(*): T}
   */
  getSite: (state) => (slug) => state.sites.find((s) => s.slug === slug),

  /**
   * Recupere les sites d'un client
   * @param state
   * @returns {function(*): *[]}
   */
  getSitesByCustomer: (state) => (customerSlug) => state.sites.filter((s) => s.customer.slug === customerSlug),

  /**
   * Liste de sites indéxés par ID
   * @param state
   * @returns {*}
   */
  siteById: (state) => _.keyBy(state.sites, 'id'),

  /**
   * Liste de tous les clients triés par ordre alphabétique
   * @param clients
   * @return {*|Array}
   */
  customers: ({ clients = [] }) => _.sortBy(clients, 'name'),

  /**
   * Liste tous les clients avec les sites mappés
   * @param state
   * @param getSitesByCustomer
   * @returns {{sites: *}[]}
   */
  allCustomers: ({ clients = [] }, { getSitesByCustomer }) => clients.map((c) => ({
    ...c,
    sites: getSitesByCustomer(c.slug)
  })),

  /**
   * Trouve un client depuis son slug
   * @param state
   * @returns {function(*): T}
   */
  getCustomer: (state) => (slug) => state.clients.find((s) => s.slug === slug),

  /**
   * Liste des clients indéxés par ID
   * @param state
   * @returns {*}
   */
  customerById: (state) => _.keyBy(state.clients, 'id'),

  /**
   * Liste des rondes mappés avec les sites et customers du store
   * @param state
   * @param getters
   * @returns {*}
   */
  allRounds: (state, getters) => state.rondes.map((r) => {
    const site = getters.siteById[r.site.id] || UNKNOWN_SITE
    const customerId = site.id === null && r.site.customer
      ? r.site.customer.id
      : site.customer.id
    return {
      ...r,
      checkpoints: Array.isArray(r.checkpoints) ? r.checkpoints : [],
      site: getters.siteById[r.site.id] || UNKNOWN_SITE,
      customer: getters.customerById[customerId] || UNKNOWN_CUSTOMER
    }
  }),

  /**
   * Liste les rondes d'un site
   * @param state
   * @param allRounds
   * @returns {function(*): *}
   */
  getSiteRounds: (state, { allRounds }) => ({ id } = {}) => allRounds.filter((r) => r.site && r.site.id === id),

  /**
   * Trouve une ronde par slug
   * @param state
   * @param allRounds
   * @returns {function(*): *}
   */
  getRoundBySlug: (state, { allRounds }) => (slug) => allRounds.find((r) => r.slug === slug),

  /**
   * Map tous les checkpoints
   * @param state
   * @param getters
   * @returns {(T | {site: (*|string|{name: string, id: null}), customer: (*|string|{name: string, id: null})})[]}
   */
  allCheckpoints: (state, getters) => state.checkpoints.map((r) => {
    const site = getters.siteById[r.site.id] || UNKNOWN_SITE
    const customerId = site.id === null && r.site.customer
      ? r.site.customer.id
      : site.customer.id
    return {
      ...r,
      site,
      customer: getters.customerById[customerId] || UNKNOWN_CUSTOMER
    }
  }),

  /**
   * Cherche un checkpoint par id
   * @param checkpoints
   * @returns {function(*): * | undefined}
   */
  getCheckpointId: ({ checkpoints = [] }) => (id) => checkpoints.find((c) => c.id === id),

  /**
   * Récupère les checkpoints d'un site
   * @param state
   * @param allCheckpoints
   * @returns {function({id: *}): *}
   */
  getSiteCheckpoints: (state, { allCheckpoints }) => ({ id }) => allCheckpoints.filter((c) => c.site.id === id),

  /**
   * Trouve un checpoint à partir de son badge
   * @param state
   * @param allCheckpoints
   * @returns {function(*): T[]}
   */
  getCheckpointByBadge: (state, { allCheckpoints }) => (badge) => allCheckpoints.find((c) => c.badge === badge),

  /**
   * Liste des Subscriptions disponibles pour les agents
   * @param agents
   * @param pricing
   * @param getters
   * @param rootState
   * @param rootGetters
   * @returns {({label: string, value: number}|{label: string, value: number})[]}
   */
  getSubscriptionOptions: ({ agents, pricing }, getters, rootState, rootGetters) => {
    const accountsLimitReached = rootGetters['user/isAccountsLimitReached']
    return [
      {
        value: 0,
        label: `Sans limite de durée - ${accountsLimitReached ? '9.99 € HT / mois' : 'GRATUIT'}`
      },
      ...[1, 2, 3, 4, 5, 10].map((value) => ({
        value,
        label: `${value} ${plz('jour', value)} - ${accountsLimitReached ? (0.5 * value).toFixed(2) + '€ HT' : 'GRATUIT'}`
      }))
    ]
  },

  /**
   * Récupère la liste des emails client
   */
  customersEmails: (state) => {
    return state.clients.map(({ email, name, logo }) => ({
      label: name,
      email,
      avatar: logo,
      type: 'customer'
    }))
  },

  /**
   * Récupère la liste des emails des contacts client
   */
  customersContactEmails: (state) => {
    const emails = []
    state.clients.forEach(({ users = [], name, logo }) =>
      emails.push(...users.map(({ email, position, profilePicture, ...user }) => ({
        label: userName(user),
        email,
        caption: [position ? `${position}` : '', name].join(' chez '),
        avatar: profilePicture || logo,
        type: 'customerContact'
      })))
    )
    return emails
  },

  /**
   * Retourne la liste des emails des agents
   * @param state
   * @param getters
   * @param rootState
   * @param rootGetters
   */
  agentsEmails: (state, getters, rootState, rootGetters) => {
    const agency = rootGetters['user/company']
    return state.agents.map(({ email, profilePicture, ...user }) => ({
      label: userName(user),
      email,
      caption: [`Agent`, agency.name].join(' chez '),
      avatar: profilePicture,
      type: 'agencyAgent'
    }))
  },

  customerEmails: (state, getters) => (slug) => {
    const mails = []
    const _customer = getters.getCustomer(slug)
    if (_customer) {
      if (_customer.email) {
        mails.push(_customer.email)
      }
      _customer.users.forEach((u) => {
        if (u.email) {
          mails.push(u.email)
        }
      })
    }
    return mails
  }
}
